import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Settings stored on a project integration; which keys exist depends on the integration type.
export interface IntegrationSettings {
  // Connection details common to most integration types.
  subdomain?: string;
  domain?: string;
  name?: string;
  email?: string;
  api_key?: string;
  ticket_status_after_escalation?: string;
  ticket_status_after_ai_response?: string;
  reply_to_note?: boolean;
  include_subject_first_response?: boolean;
  welcome_message?: string;
  webhook_url?: string;
  author_display_name?: string;
  author_avatar_url?: string;
  // Zendesk OAuth: auth_type 'oauth', auth_status 'ok' once authorized.
  auth_type?: string;
  auth_status?: string;
  // Ticket fields passed to the AI as context (Zendesk and Freshdesk email).
  include_custom_fields?: boolean;
  custom_fields?: { id: string; name: string }[] | null;
  // Type-specific connection details.
  chat_url?: string;
  agency_id?: string;
  credits?: number;
  auth_email?: string;
  api_token?: string;
  via_email?: boolean;
  via_chat?: boolean;
  app_id?: string;
  conversation_key_id?: string;
  client_id?: string;
  client_secret?: string;
  scope?: string;
  server_locale?: string;
  code?: string;
  refresh_token?: string;
  slack_url?: string;
  // Freshchat webhook token, and the Zendesk chat responder ('default_responder' or 'handoff').
  webhook_api_token?: string;
  responder_mode?: string;
  [key: string]: unknown;
}

// The integration being set up in the integrations modal: an entry from the integration
// catalog (IntegrationTypes), merged with the project integration once it has been created.
export interface SelectedIntegration {
  friendlyName?: string;
  icon?: string;
  setupGuideUrl?: string | null;
  setupSteps?: number;
  data?: { integration_type: string; integration_data?: Record<string, unknown> };
  id?: number | string;
  integration_type?: string;
  integration_data?: IntegrationSettings;
  status?: string;
  [key: string]: unknown;
}

interface IntegrationState {
  selectedIntegration: SelectedIntegration | null;
  openFinalPopup: boolean;
}

const initialState: IntegrationState = {
  selectedIntegration: null,
  openFinalPopup: false,
};

export const integrationSlices = createSlice({
  name: 'integrationSlices',
  initialState,
  reducers: {
    setSelectedIntegration(state, action: PayloadAction<SelectedIntegration | null>) {
      state.openFinalPopup = false;
      state.selectedIntegration = action.payload;
    },
    setOpenFinalPopup(state, action: PayloadAction<boolean>) {
      state.openFinalPopup = action.payload;
    },
  },
});

export const { setSelectedIntegration, setOpenFinalPopup } = integrationSlices.actions;

export default integrationSlices.reducer;
